import { useState } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 2L-A24 — Reopen Report for Correction (admin only).
 *
 * Renders on the Lab Detail page when the test is `approved` or `published`.
 * Confirmation modal must be accepted before the POST fires.
 */
export function ReopenForCorrectionCard({ test, reload }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      await api.post(`/admin/lab/tests/${test.id}/reopen-for-correction`, { reason });
      toast.success("Report reopened — results are now editable");
      setOpen(false);
      setReason("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to reopen report");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 rounded-2xl bg-rose-50 border border-rose-200 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="font-semibold text-rose-900 text-sm">Need to correct this report?</h3>
          <p className="text-xs text-rose-800 mt-1 leading-relaxed">
            If a parameter was entered incorrectly or missed, you can reopen this {test.status} report so staff can edit, regenerate the COA/Summary, and republish a corrected version.
          </p>
          <button
            data-testid="reopen-for-correction-btn"
            onClick={() => setOpen(true)}
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reopen Report for Correction
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setOpen(false)}>
          <div data-testid="reopen-confirm-modal" onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <h2 className="font-display text-xl font-bold text-slate-900">Reopen this report for correction?</h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Reopening this report will move it out of its <strong>{test.status}</strong> state so results can be corrected. The customer-facing report should not be resent until the corrected COA and Summary have been regenerated, reviewed and republished.
            </p>
            <label className="block mt-5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Reason (optional but recommended)</span>
              <textarea
                data-testid="reopen-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Sulphate entered as 0 in error — should be 22 mg/L"
                rows={3}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button
                data-testid="reopen-cancel-btn"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
              >Cancel</button>
              <button
                data-testid="reopen-confirm-btn"
                onClick={confirm}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-semibold"
              >{busy ? "Reopening…" : "Reopen for Correction"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReopenForCorrectionCard;
